/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
// import Table, { TableProps } from 'antd/lib/table';
import { message, Table } from 'antd';
import { TableRowSelection } from 'antd/lib/table/interface';
import React, { Fragment, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import IconButton from '~components/_common/IconButton/IconButton';
import { useModuleFiles } from '~src/hooks/api-hooks/useModuleFiles';
import { styleFn } from './FileTable.style';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { SearchContext } from '~src/contexts/SearchContext';

import {
  dbModuleFileToTableEntry,
  sortByStringProp,
  sortByType,
  TableEntry,
} from './FileTable.utils';
import { TableProps } from 'antd/lib/table';
import { filterByQuery } from '~src/utils/searchFilters';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';

export interface ModuleTableProps extends TableProps<TableEntry> {
  selectedKeys?: TableRowSelection<TableEntry>['selectedRowKeys'];
  onSelect?: TableRowSelection<TableEntry>['onSelect'];
  onSelectAll?: TableRowSelection<TableEntry>['onSelectAll'];
  highlightedRow?: TableEntry;
  openSVFTranscribe?: (fileToView: any) => void;
}

const sortByName = sortByStringProp('name');
const sortByDescription = sortByStringProp('description');

export const ModuleTable: React.FC<ModuleTableProps> = ({
  selectedKeys,
  onSelect,
  onSelectAll,
  highlightedRow,
  openSVFTranscribe,
  ...tableProps
}) => {
  const { moduleId } = useParams<{ moduleId: string }>();

  const intl = useIntl();
  const styles = useStyles(styleFn);
  const { setFileToView } = useFileViewerContext();
  const searchContext = useContext(SearchContext);

  const moduleFiles = useModuleFiles(
    { module: Number(moduleId), take: 20, withVersions: false },
    { enabled: true }
  );

  const moduleFilesSource = useMemo(
    () => (moduleFiles.data || []).map(dbModuleFileToTableEntry),
    [moduleFiles.data]
  );

  const filteredSource = [
    ...moduleFilesSource.filter((item) => {
      if (
        filterByQuery(item.name, searchContext.query) ||
        filterByQuery(item.description, searchContext.query) ||
        filterByQuery(item.type.icon, searchContext.query) ||
        filterByQuery(item.sector, searchContext.query)
      ) {
        return item;
      }
      return null;
    }),
  ];

  const columns = useMemo<TableProps<TableEntry>['columns']>(
    () => [
      {
        title: intl.formatMessage({ id: 'type' }),
        dataIndex: 'type',
        width: 75,
        align: 'center',
        render: (type, record) => (
          <IconButton
            iconStyle={type.iconStyle}
            icon={type.icon}
            customStyle={styles.icons}
            color={type.color}
            tooltip={intl.formatMessage(
              { id: type.icon },
              { type: record.extension?.toUpperCase() }
            )}
            badge={type.is_empty && '0'}
            backgroundOff
          />
        ),
      },
      {
        title: intl.formatMessage({ id: 'action' }),
        width: 72,
        align: 'center',
        dataIndex: 'type',

        render: (type, record) =>
          (type.icon === 'file-pdf' || type.icon === 'file-image') && (
            <IconButton
              click={() => {
                setFileToView(record.dbModuleFile);
              }}
              iconStyle={type.iconStyle}
              icon={['far', 'eye']}
              color="red"
            />
          ),
      },
      {
        title: intl.formatMessage({ id: 'name' }),
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
        width: 250,
        ellipsis: true,
        render: (name, record) => (
          <Fragment>
            {name}
            {record.favourite && (
              <IconButton icon={['fas', 'star']} color="yellow" backgroundOff />
            )}
          </Fragment>
        ),
      },
      {
        title: intl.formatMessage({ id: 'description' }),
        dataIndex: 'description',
        sorter: (a, b) => {
          if (!a.description && !b.description) return 0;
          if (!a.description) return -1;
          if (!b.description) return 1;
          return (a.description || '').localeCompare(b.description || '');
        },
        width: 200,
        ellipsis: true,
      },
      {
        title: intl.formatMessage({ id: 'sector' }),
        dataIndex: 'sector',
        width: 100,
        ellipsis: true,
      },
      {
        title: intl.formatMessage({ id: 'recipient' }),
        dataIndex: 'recipient',
        width: 100,
        ellipsis: true,
      },
      {
        title: intl.formatMessage({ id: 'subject' }),
        dataIndex: 'subject',
        width: 100,
        ellipsis: true,
      },
    ],
    [intl]
  );

  return (
    <div>
      <Table<TableEntry>
        showSorterTooltip={false}
        css={[
          styles.table,
          tableProps.scroll?.y && {
            '.ant-table-body': {
              height: tableProps.scroll.y,
            },
          },
        ]}
        className="main-content-window"
        pagination={false}
        bordered
        columns={columns}
        dataSource={filteredSource}
        loading={moduleFiles.isLoading}
        rowClassName={(record) => {
          let className = '';
          if (record.key === highlightedRow?.key) {
            className += 'ant-table-row-selected' + ' ';
          }
          if (record.disabled) {
            className += 'unfinished' + ' ';
          }

          return className;
        }}
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedKeys,
          columnWidth: 50,
          onSelect,
          onSelectAll,
        }}
        {...tableProps}
      />
    </div>
  );
};
